import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["popover", "list", "form", "count", "name", "description"]
  static values = {
    cardId: Number,
    projectId: Number,
    createUrl: String,
    toggleUrl: String
  }

  connect() {
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleOtherPopoverOpen = this.handleOtherPopoverOpen.bind(this)
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('popover:open', this.handleOtherPopoverOpen)
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('popover:open', this.handleOtherPopoverOpen)
  }

  handleOtherPopoverOpen(event) {
    if (event.detail.controller !== this) {
      this.close()
    }
  }

  handleClickOutside(event) {
    if (this.hasPopoverTarget && !this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    event.stopPropagation()
    if (this.hasPopoverTarget) {
      const wasHidden = this.popoverTarget.classList.contains('hidden')
      this.popoverTarget.classList.toggle('hidden')
      if (wasHidden) {
        document.dispatchEvent(new CustomEvent('popover:open', { detail: { controller: this } }))
      }
    }
  }

  close() {
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.add('hidden')
      this.hideForm()
    }
  }

  showForm(event) {
    event.preventDefault()
    if (this.hasFormTarget && this.hasListTarget) {
      this.listTarget.classList.add('hidden')
      this.formTarget.classList.remove('hidden')
      if (this.hasNameTarget) {
        this.nameTarget.focus()
      }
    }
  }

  hideForm() {
    if (this.hasFormTarget && this.hasListTarget) {
      this.formTarget.classList.add('hidden')
      this.listTarget.classList.remove('hidden')
      this.clearForm()
    }
  }

  clearForm() {
    if (this.hasNameTarget) this.nameTarget.value = ''
    if (this.hasDescriptionTarget) this.descriptionTarget.value = ''
  }

  async createLocation(event) {
    event.preventDefault()

    const name = this.hasNameTarget ? this.nameTarget.value.trim() : ''
    const description = this.hasDescriptionTarget ? this.descriptionTarget.value.trim() : ''

    if (!name) {
      alert('Location name is required')
      return
    }

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          location: { name: name, description: description }
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.addLocationToList(data.location)
        this.hideForm()
        this.toggleLocation(data.location.id)
      } else {
        const error = await response.json()
        alert(error.errors?.join(', ') || 'Failed to create location')
      }
    } catch (err) {
      console.error('Failed to create location:', err)
    }
  }

  addLocationToList(location) {
    const listContainer = this.listTarget.querySelector('[data-location-list]')
    if (listContainer) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'w-full text-left px-3 py-2 rounded-lg text-white bg-emerald-500 hover:bg-emerald-600 transition mb-2'
      button.dataset.locationId = location.id
      button.dataset.action = 'click->location-popover#toggleLocationClick'
      button.textContent = location.name
      listContainer.insertBefore(button, listContainer.querySelector('[data-add-new]'))
    }
  }

  async toggleLocationClick(event) {
    event.preventDefault()
    const button = event.currentTarget
    const locationId = button.dataset.locationId
    const success = await this.toggleLocation(locationId)

    if (success) {
      if (button.classList.contains('bg-emerald-500')) {
        button.classList.remove('bg-emerald-500', 'hover:bg-emerald-600', 'text-white')
        button.classList.add('bg-gray-100', 'hover:bg-gray-200', 'text-gray-700')
      } else {
        button.classList.remove('bg-gray-100', 'hover:bg-gray-200', 'text-gray-700')
        button.classList.add('bg-emerald-500', 'hover:bg-emerald-600', 'text-white')
      }
    }
  }

  async toggleLocation(locationId) {
    try {
      const response = await fetch(this.toggleUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ location_id: locationId })
      })

      if (response.ok) {
        const data = await response.json()
        if (this.hasCountTarget) {
          this.countTarget.textContent = data.count > 0 ? data.count : ''
        }
        return true
      }
      return false
    } catch (err) {
      console.error('Failed to toggle location:', err)
      return false
    }
  }
}
